import { useCallback, useState } from 'react';
import { resolveApiErrorMessage } from '../utils/apiErrorMessages';
import { useTranslation } from 'react-i18next';
import { documentService } from '@services/document.service';
import {
  DEFAULT_COMPARISON_TYPE,
  MAX_SEQUENCE_IMAGES,
  type ComparisonType,
} from '@constants/documentDetail';
import type { DocumentImage } from './useDocumentImages';

export type ComparisonMode = 'compare' | 'sequence';

export interface ImageComparisonState {
  selectedImages: string[];
  comparisonMode: ComparisonMode | null;
  comparisonType: ComparisonType;
  comparisonResult: string | null;
  comparingImages: boolean;
  dialogOpen: boolean;
  setComparisonType: (type: ComparisonType) => void;
  toggleImage: (imageId: string) => void;
  clearSelection: () => void;
  startComparison: (mode: ComparisonMode) => void;
  closeDialog: () => void;
}

export function useImageComparison(
  id: string | undefined,
  images: DocumentImage[],
): ImageComparisonState {
  const { t } = useTranslation();
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode | null>(null);
  const [comparisonType, setComparisonType] = useState<ComparisonType>(DEFAULT_COMPARISON_TYPE);
  const [comparisonResult, setComparisonResult] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comparingImages, setComparingImages] = useState(false);

  const toggleImage = useCallback((imageId: string) => {
    setSelectedImages((prev) =>
      prev.includes(imageId) ? prev.filter((entry) => entry !== imageId) : [...prev, imageId],
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedImages([]);
    setComparisonMode(null);
  }, []);

  const executeComparison = useCallback(
    async (mode: ComparisonMode) => {
      if (!id) return;

      try {
        setComparingImages(true);
        setComparisonResult(null);
        setDialogOpen(true);

        if (mode === 'compare') {
          const result = await documentService.compareImages(
            id,
            selectedImages[0],
            selectedImages[1],
            comparisonType,
          );
          setComparisonResult(result.analysis);
        } else {
          const sortedImageIds = [...selectedImages].sort((a, b) => {
            const imgA = images.find((img) => img.id === a);
            const imgB = images.find((img) => img.id === b);
            return (imgA?.pageNumber || 0) - (imgB?.pageNumber || 0);
          });

          const result = await documentService.analyzeImageSequence(id, sortedImageIds);
          setComparisonResult(result.analysis);
        }
      } catch (error) {
        console.error('Failed to analyze images:', error);
        alert(resolveApiErrorMessage(t, error, { fallbackKey: 'documents.imageAnalysisFailed' }));
        setDialogOpen(false);
      } finally {
        setComparingImages(false);
      }
    },
    [id, images, selectedImages, comparisonType, t],
  );

  const startComparison = useCallback(
    (mode: ComparisonMode) => {
      if (mode === 'compare' && selectedImages.length !== 2) {
        alert(t('documents.selectTwoImages'));
        return;
      }
      if (mode === 'sequence' && selectedImages.length < 2) {
        alert(t('documents.selectAtLeastTwoImages'));
        return;
      }
      if (mode === 'sequence' && selectedImages.length > MAX_SEQUENCE_IMAGES) {
        alert(t('documents.sequenceAnalysisMaxImages', { count: MAX_SEQUENCE_IMAGES }));
        return;
      }
      setComparisonMode(mode);
      executeComparison(mode);
    },
    [selectedImages, executeComparison, t],
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setComparisonResult(null);
    setComparisonMode(null);
    clearSelection();
  }, [clearSelection]);

  return {
    selectedImages,
    comparisonMode,
    comparisonType,
    comparisonResult,
    comparingImages,
    dialogOpen,
    setComparisonType,
    toggleImage,
    clearSelection,
    startComparison,
    closeDialog,
  };
}
